import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Select — accessible custom <select> replacement (ARIA listbox pattern).
 *
 * Controlled component. Trigger is a button[aria-haspopup=listbox], the panel
 * is a ul[role=listbox] with li[role=option][aria-selected], and the active
 * option is tracked via aria-activedescendant. Full keyboard support: Arrow
 * Up/Down, Enter/Space, Escape (returns focus to the button), Home/End, Tab
 * (closes), and type-ahead. Closes on outside click.
 *
 * Styled from the journal design tokens so it matches the `.input` control in
 * both light and dark themes (the app toggles a `.dark` class on <html>).
 */

type Opt = { value: string; label: string };
export type SelectOption = Opt | string;

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  label?: string;
  id?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

function normalize(options: SelectOption[]): Opt[] {
  return options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
}

export default function Select({
  value,
  onChange,
  options,
  label,
  id,
  placeholder = "Select…",
  className = "",
  disabled = false,
}: Props) {
  const opts = useMemo(() => normalize(options), [options]);
  const reactId = useId();
  const baseId = id ?? reactId;
  const listId = `${baseId}-list`;
  const labelId = label ? `${baseId}-label` : undefined;

  const rootRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const optRefs = useRef<Array<HTMLLIElement | null>>([]);
  const typeahead = useRef<{ str: string; at: number }>({ str: "", at: 0 });

  const selectedIdx = opts.findIndex((o) => o.value === value);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(selectedIdx >= 0 ? selectedIdx : 0);

  const selected = selectedIdx >= 0 ? opts[selectedIdx] : undefined;
  const optId = (i: number) => `${baseId}-opt-${i}`;

  const [coords, setCoords] = useState<{ left: number; top: number; width: number } | null>(null);
  const reposition = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setCoords({ left: r.left, top: r.bottom + 4, width: r.width });
  }, []);

  const openList = useCallback(() => {
    if (disabled) return;
    setActiveIdx(selectedIdx >= 0 ? selectedIdx : 0);
    reposition();
    setOpen(true);
  }, [disabled, selectedIdx, reposition]);

  const closeList = useCallback((focusBtn: boolean) => {
    setOpen(false);
    if (focusBtn) btnRef.current?.focus();
  }, []);

  const choose = useCallback(
    (i: number) => {
      const opt = opts[i];
      if (!opt) return;
      onChange(opt.value);
      closeList(true);
    },
    [opts, onChange, closeList],
  );

  // Move active option into view when it changes while open.
  useEffect(() => {
    if (!open) return;
    optRefs.current[activeIdx]?.scrollIntoView({ block: "nearest" });
  }, [open, activeIdx]);

  // Focus the listbox when it opens so it receives key events.
  useEffect(() => {
    if (open) listRef.current?.focus();
  }, [open]);

  // Close on outside click. The list is portaled to <body>, so treat clicks
  // inside EITHER the control or the portaled list as "inside".
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!rootRef.current?.contains(t) && !listRef.current?.contains(t)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
  }, [open]);

  // Keep the portaled panel pinned to the button on scroll / resize.
  useEffect(() => {
    if (!open) return;
    reposition();
    const onMove = () => reposition();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, reposition]);

  const typeAhead = useCallback(
    (ch: string) => {
      const now = Date.now();
      const t = typeahead.current;
      t.str = now - t.at > 600 ? ch : t.str + ch;
      t.at = now;
      const q = t.str.toLowerCase();
      // Prefer full accumulated string, else cycle from after current on a single char.
      const start = q.length > 1 ? 0 : activeIdx + 1;
      for (let k = 0; k < opts.length; k++) {
        const idx = (start + k) % opts.length;
        if (opts[idx].label.toLowerCase().startsWith(q)) {
          setActiveIdx(idx);
          return;
        }
      }
    },
    [opts, activeIdx],
  );

  const onListKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIdx((i) => (i + 1) % opts.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIdx((i) => (i - 1 + opts.length) % opts.length);
        break;
      case "Home":
        e.preventDefault();
        setActiveIdx(0);
        break;
      case "End":
        e.preventDefault();
        setActiveIdx(opts.length - 1);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        choose(activeIdx);
        break;
      case "Escape":
        e.preventDefault();
        closeList(true);
        break;
      case "Tab":
        closeList(false);
        break;
      default:
        if (e.key.length === 1 && /\S/.test(e.key)) typeAhead(e.key);
    }
  };

  const onBtnKeyDown = (e: React.KeyboardEvent) => {
    if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
      e.preventDefault();
      openList();
    }
  };

  return (
    <div ref={rootRef} className={`fp-select ${className}`}>
      {label && (
        <span id={labelId} className="label mb-2 block">
          {label}
        </span>
      )}
      <button
        ref={btnRef}
        type="button"
        id={`${baseId}-btn`}
        className="fp-select__btn input"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-labelledby={labelId ? `${labelId} ${baseId}-btn` : undefined}
        disabled={disabled}
        onClick={() => (open ? closeList(true) : openList())}
        onKeyDown={onBtnKeyDown}
      >
        <span className={`fp-select__value${selected ? "" : " is-placeholder"}`}>
          {selected ? selected.label : placeholder}
        </span>
        <svg
          className={`fp-select__chev${open ? " is-open" : ""}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && coords &&
        createPortal(
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            tabIndex={-1}
            aria-labelledby={labelId ?? `${baseId}-btn`}
            aria-activedescendant={optId(activeIdx)}
            className="fp-select__list fp-select__list--portal"
            style={{ left: coords.left, top: coords.top, width: coords.width }}
            onKeyDown={onListKeyDown}
          >
          {opts.map((o, i) => {
            const isSelected = o.value === value;
            const isActive = i === activeIdx;
            return (
              <li
                key={o.value}
                ref={(el) => {
                  optRefs.current[i] = el;
                }}
                id={optId(i)}
                role="option"
                aria-selected={isSelected}
                className={`fp-select__opt${isActive ? " is-active" : ""}${isSelected ? " is-selected" : ""}`}
                onClick={() => choose(i)}
                onMouseMove={() => setActiveIdx(i)}
              >
                <span className="fp-select__opt-label">{o.label}</span>
                <svg
                  className="fp-select__check"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.6}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12l4 4 10-10" />
                </svg>
              </li>
            );
          })}
          </ul>,
          document.body,
        )}
    </div>
  );
}
